import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useWfUserService from 'src/api/wf/service/WfUserService';
import { useWfUserStore } from 'src/api/wf/stores/WfUserStore';
import Button from 'src/ui/wf/common/button/Button';
import { userColumns } from './UserColumn';
import { FormRow } from '../../common/form/FormRow';
import Input from '../../common/input/Input';

const backButtonClassName = `
  w-fit whitespace-nowrap rounded-md border border-gray-400
  bg-gradient-to-b from-gray-100 to-gray-300 px-2 text-gray-800
`;

const WF_UserSearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getUsersByNameLdapPhoneNo } = useWfUserService();
  const [name, setName] = useState<string | undefined>(undefined);
  const [ldapId, setLdapId] = useState<string | undefined>(undefined);
  const [telephoneNumber, setTelephoneNumber] = useState<string | undefined>(undefined);
  const [hasSearched, setHasSearched] = useState(false);
  const { users, setSelectedUser, setUsers } = useWfUserStore();

  const createInputHandler =
    (
      setter: Dispatch<SetStateAction<string | undefined>>
    ): ((event: ChangeEvent<HTMLInputElement>) => void) =>
    (event) => {
      setter(event.target.value === '' ? undefined : event.target.value);
    };

  const handleNameChanged = createInputHandler(setName);
  const handleLdapIdChanged = createInputHandler(setLdapId);
  const handlePhoneNoChanged = createInputHandler(setTelephoneNumber);

  const handleSearch = async () => {
    setHasSearched(true);
    if (name || ldapId || telephoneNumber) {
      getUsersByNameLdapPhoneNo(name, ldapId, telephoneNumber);
    } else {
      setUsers([]);
    }
  };

  const us = [...users].sort((a, b) => a.id.localeCompare(b.id)).slice(0, 50);

  useEffect(() => {
    setUsers([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="h-full space-y-4 p-4">
      <div className="flex justify-end">
        <Button
          title={t('wf.common.back')}
          onClick={() => {
            navigate(-1);
          }}
          className={backButtonClassName}
        />
      </div>
      <div className="space-y-4 p-4">
        <FormRow title={t('wf.userSearch.userName')}>
          <Input value={name || ''} onChange={handleNameChanged} />
        </FormRow>
        <FormRow title={t('wf.userSearch.ldapId')}>
          <Input value={ldapId || ''} onChange={handleLdapIdChanged} />
        </FormRow>
        <FormRow title={t('wf.userSearch.phoneNo')}>
          <Input value={telephoneNumber || ''} onChange={handlePhoneNoChanged} />
        </FormRow>
        <div className="flex justify-end pt-2">
          <Button
            title={t('wf.searchHeader.search')}
            className="w-fit whitespace-nowrap rounded-md border border-gray-400
                         bg-gradient-to-b from-gray-100 to-gray-300 p-1 text-gray-800 
                         disabled:cursor-not-allowed disabled:border-gray-300 disabled:from-gray-200 
                         disabled:to-gray-200 disabled:text-gray-400 disabled:shadow-none"
            onClick={handleSearch}
          />
        </div>
      </div>

      {hasSearched && !name && !ldapId && !telephoneNumber && (
        <p className="text-center text-sm text-red-600">{t('wf.userSearch.errorMessage')}</p>
      )}

      {hasSearched && (
        <div>
          <DataTable
            data={us}
            noDataComponent={t('wf.common.noResults')}
            columns={userColumns(navigate, t, setSelectedUser)}
          />
        </div>
      )}

      <div className="flex justify-end">
        <Button
          title={t('wf.common.back')}
          onClick={() => {
            navigate(-1);
          }}
          className={backButtonClassName}
        />
      </div>
    </div>
  );
};

export default WF_UserSearch;
